import React, { useEffect, useMemo, useState } from 'react';
import { Clock, User } from '@phosphor-icons/react';
import { taskService } from '../../../../services/tasks/taskService.js';

const ACTION_LABELS = {
    CREAR_TAREA: 'Creada',
	REACTIVAR_TAREA: 'Reactivada',
	EN_CAMINO: 'En camino',
	INICIAR_TAREA: 'Iniciada',
	FORMULARIO_COMPLETADO: 'Formulario',
	ACTUALIZAR_TAREA: 'Actualizada',
	FINALIZAR_TAREA: 'Finalizada',
    CANCELAR_TAREA: 'Cancelada',
    VALIDAR_TAREA: 'Validada',
    REASIGNAR_EQUIPO: 'Reasignada',
};

// Mapea cada acción del historial al estado de tarea equivalente, para
// reusar la misma semántica de color que el resto de la app (taskColors.js)
// en vez de pintar todos los eventos del mismo color.
const ACTION_STATUS = {
    CREAR_TAREA: 'pendiente',
	REACTIVAR_TAREA: 'pendiente',
	EN_CAMINO: 'en_camino',
	INICIAR_TAREA: 'iniciada',
	FINALIZAR_TAREA: 'finalizada',
	VALIDAR_TAREA: 'finalizada',
    CANCELAR_TAREA: 'cancelada',
    REASIGNAR_EQUIPO: 'relevada',
};

const NEUTRAL_BADGE_CLASS = 'border border-line text-content-muted bg-surface-2';
const NEUTRAL_DOT_CLASS = 'bg-content-muted';

const STATUS_BADGE_CLASS = {
	pendiente: 'border border-st-pendiente/30 text-st-pendiente bg-st-pendiente/12',
	en_camino: 'border border-st-en-camino/30 text-st-en-camino bg-st-en-camino/12',
	iniciada: 'border border-st-en-camino/30 text-st-en-camino bg-st-en-camino/12',
	relevada: 'border border-st-relevada/30 text-st-relevada bg-st-relevada/12',
	finalizada: 'border border-st-finalizada/30 text-st-finalizada bg-st-finalizada/12',
	cancelada: 'border border-st-cancelada/30 text-st-cancelada bg-st-cancelada/12',
};

const STATUS_DOT_CLASS = {
	pendiente: 'bg-st-pendiente',
	en_camino: 'bg-st-en-camino',
	iniciada: 'bg-st-en-camino',
	relevada: 'bg-st-relevada',
	finalizada: 'bg-st-finalizada',
	cancelada: 'bg-st-cancelada',
};

const getConnectorStyle = (index, totalSegments) => {
	if (totalSegments <= 0) {
		return { backgroundColor: 'rgb(var(--app-border-rgb))' };
	}

	const progress = index / totalSegments;
	const tone = Math.round(255 - (255 - 120) * progress);
	return { backgroundColor: `rgb(${tone}, ${tone}, ${tone})` };
};

const getActionInfo = (action) => {
	const status = ACTION_STATUS[action];

	return {
		label: ACTION_LABELS[action] || action || 'Acción',
		badgeClassName: status ? STATUS_BADGE_CLASS[status] : NEUTRAL_BADGE_CLASS,
		dotClassName: status ? STATUS_DOT_CLASS[status] : NEUTRAL_DOT_CLASS
	};
};

const formatDate = (dateString) => {
	if (!dateString) {
		return 'Sin fecha';
	}

	const date = new Date(dateString);
	if (Number.isNaN(date.getTime())) {
		return 'Sin fecha';
	}

	return new Intl.DateTimeFormat('es-AR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit'
	}).format(date);
};

function HistoryTab({ taskId }) {
	const [historyItems, setHistoryItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [reloadKey, setReloadKey] = useState(0);
	const [sortOrder, setSortOrder] = useState('desc');

	useEffect(() => {
		if (!taskId) {
			setHistoryItems([]);
			setLoading(false);
			setError('No se encontró el identificador de la tarea.');
			return;
		}

		let cancelled = false;

		const fetchHistory = async () => {
			try {
				setLoading(true);
				setError(null);

				const normalizedItems = await taskService.getTaskActivityHistory(taskId);

				if (cancelled) {
					return;
				}

				setHistoryItems(normalizedItems);
			} catch (fetchError) {
				if (cancelled) {
					return;
				}

				setError('No se pudo cargar el historial de actividad de la tarea.');
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		fetchHistory();

		return () => {
			cancelled = true;
		};
	}, [taskId, reloadKey]);

	const sortedItems = useMemo(() => {
		const sorted = [...historyItems].sort((firstItem, secondItem) => {
			const firstDate = new Date(firstItem?.timestamp || firstItem?.createdAt || 0).getTime();
			const secondDate = new Date(secondItem?.timestamp || secondItem?.createdAt || 0).getTime();
			return sortOrder === 'asc' ? firstDate - secondDate : secondDate - firstDate;
		});

		return sorted;
	}, [historyItems, sortOrder]);

	if (loading) {
		return (
			<div className="space-y-4">
				{[1, 2, 3].map((item) => (
					<div key={item} className="animate-pulse rounded-2xl border border-line bg-bg/70 p-5">
						<div className="mb-3 h-5 w-44 rounded bg-surface-2" />
						<div className="h-4 w-24 rounded bg-surface-2" />
					</div>
				))}
			</div>
		);
	}

	if (error) {
		return (
			<div className="rounded-3xl border border-red-800/70 bg-red-950/20 p-8 text-center">
				<p className="text-lg font-semibold text-red-200">{error}</p>
				<button
					type="button"
					onClick={() => setReloadKey((current) => current + 1)}
					className="mt-4 rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-content transition-colors hover:bg-red-500"
				>
					Reintentar
				</button>
			</div>
		);
	}

	if (sortedItems.length === 0) {
		return (
			<div className="space-y-4">
				<div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-bg/50 px-4 py-3">
					<div className="inline-flex items-center gap-2 text-sm text-content">
						<Clock size={16} />
						<span className="font-medium">Orden cronológico</span>
						<select
							value={sortOrder}
							onChange={(event) => setSortOrder(event.target.value)}
							className="rounded-lg border border-line bg-surface px-3 py-1 text-sm text-content outline-none transition-colors hover:border-line"
						>
							<option value="desc">Más reciente primero</option>
							<option value="asc">Más antiguas primero</option>
						</select>
					</div>

					<p className="text-sm font-medium text-content">0 eventos registrados</p>
				</div>

				<div className="rounded-3xl border border-line bg-surface/70 p-8 text-center text-content">
					No hay historial de actividad para esta tarea.
				</div>
			</div>
		);
	}

	return (
		<div className="relative space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-bg/50 px-4 py-3">
				<div className="inline-flex items-center gap-2 text-sm text-content">
					<Clock size={16} />
					<span className="font-medium">Orden cronológico</span>
					<select
						value={sortOrder}
						onChange={(event) => setSortOrder(event.target.value)}
						className="rounded-lg border border-line bg-surface px-3 py-1 text-sm text-content outline-none transition-colors hover:border-line"
					>
						<option value="desc">Más reciente primero</option>
						<option value="asc">Más antiguas primero</option>
					</select>
				</div>

				<p className="text-sm font-medium text-content">{sortedItems.length} eventos registrados</p>
			</div>

			<div className="space-y-5">
				{sortedItems.map((item, index) => {
					const actionInfo = getActionInfo(item?.action);
					const itemDate = item?.timestamp || item?.createdAt;
					const isFirst = index === 0;
					const isLast = index === sortedItems.length - 1;
					const connectorStyle = getConnectorStyle(index, sortedItems.length - 1);

					return (
						<div key={item?.id || `${item?.action || 'action'}-${itemDate || index}-${index}`} className="relative pl-16">
							{!isLast ? <div className="absolute left-[35px] top-[16px] h-[calc(100%+1.25rem)] w-px" style={connectorStyle} /> : null}

							<div className={`absolute left-[21px] top-1 h-7 w-7 rounded-full ${actionInfo.dotClassName} border-4 border-line flex items-center justify-center`}>
								{isFirst ? <div className="h-2 w-2 animate-pulse rounded-full bg-white" /> : null}
							</div>

							<div className="rounded-2xl border border-line bg-surface/70 p-4 transition-colors hover:bg-bg/80">
								<div className="mb-2 flex flex-wrap items-center justify-between gap-2">
									<span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${actionInfo.badgeClassName}`}>
										{actionInfo.label}
									</span>

									<div className="inline-flex items-center gap-1 text-xs text-content-muted">
										<Clock size={14} />
										<span>{formatDate(itemDate)}</span>
									</div>
								</div>

								<p className="mb-2 text-sm text-content">{item?.description || 'Sin descripción'}</p>

								<div className="inline-flex items-center gap-1 text-xs text-content-muted">
									<User size={14} />
									<span className="font-mono">{item?.userId || item?.user?.email || item?.user?.name || 'Sin usuario'}</span>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}

export default HistoryTab;
